import {
  getDocs,
  getDoc,
  collection,
  updateDoc,
  doc,
  deleteField,
} from 'firebase/firestore';
import { db } from '../../../firebase-client';

// Extract all tag names from a flat tag array
export function extractAllTagNames(tags = []) {
  return new Set(
    (Array.isArray(tags) ? tags : [])
      .filter((tag) => tag?.type !== 'ageRule' && tag?.systemManaged !== true)
      .map((tag) => (typeof tag?.name === 'string' ? tag.name.trim() : ''))
      .filter(Boolean)
  );
}

export function getDeletedTagNames(oldTags = [], newTags = []) {
  const oldSet = extractAllTagNames(oldTags);
  const newSet = extractAllTagNames(newTags);
  return [...oldSet].filter((tag) => !newSet.has(tag));
}

function normalizeOverrideMap(rawMap) {
  return rawMap && typeof rawMap === 'object' && !Array.isArray(rawMap) ? rawMap : {};
}

function pruneOverrideMap(rawMap, deletedSet) {
  const source = normalizeOverrideMap(rawMap);
  const next = {};
  let changed = false;

  Object.entries(source).forEach(([rawName, amount]) => {
    const name = typeof rawName === 'string' ? rawName.trim() : '';
    if (!name) {
      changed = true;
      return;
    }
    if (deletedSet.has(name)) {
      changed = true;
      return;
    }
    next[name] = amount;
    if (name !== rawName) changed = true;
  });

  if (Object.keys(source).length !== Object.keys(next).length) changed = true;
  return { next, changed };
}

export function extractPricingOverrideTagNames({ stripeSetup = {}, subscriptions = {} } = {}) {
  const stripeMonthly = normalizeOverrideMap(stripeSetup?.tagOverrides);
  const stripeYearly = normalizeOverrideMap(stripeSetup?.tagOverridesYearly);
  const subsMonthly = normalizeOverrideMap(subscriptions?.tagOverrides);
  const subsYearly = normalizeOverrideMap(subscriptions?.tagOverridesYearly);

  return new Set([
    ...Object.keys(stripeMonthly),
    ...Object.keys(stripeYearly),
    ...Object.keys(subsMonthly),
    ...Object.keys(subsYearly),
  ]);
}

export async function pruneDeletedTagsFromPricingOverrides(teamId, deletedTagNames = []) {
  const deletedSet = new Set(
    (Array.isArray(deletedTagNames) ? deletedTagNames : [])
      .map((name) => (typeof name === 'string' ? name.trim() : ''))
      .filter(Boolean)
  );
  if (!teamId || deletedSet.size === 0) return;

  const teamRef = doc(db, 'teams', teamId);
  const stripeSetupRef = doc(db, 'teams', teamId, 'stripe', 'setup');
  const [teamSnap, stripeSetupSnap] = await Promise.all([
    getDoc(teamRef),
    getDoc(stripeSetupRef),
  ]);

  const teamData = teamSnap.exists() ? (teamSnap.data() || {}) : {};
  const subscriptions = teamData?.subscriptions || {};
  const stripeSetup = stripeSetupSnap.exists() ? (stripeSetupSnap.data() || {}) : {};

  const stripeMonthly = pruneOverrideMap(stripeSetup?.tagOverrides, deletedSet);
  const stripeYearly = pruneOverrideMap(stripeSetup?.tagOverridesYearly, deletedSet);
  if (stripeSetupSnap.exists() && (stripeMonthly.changed || stripeYearly.changed)) {
    await updateDoc(stripeSetupRef, {
      tagOverrides:
        Object.keys(stripeMonthly.next).length > 0 ? stripeMonthly.next : deleteField(),
      tagOverridesYearly:
        Object.keys(stripeYearly.next).length > 0 ? stripeYearly.next : deleteField(),
    });
  }

  const subsMonthly = pruneOverrideMap(subscriptions?.tagOverrides, deletedSet);
  const subsYearly = pruneOverrideMap(subscriptions?.tagOverridesYearly, deletedSet);
  if (subsMonthly.changed || subsYearly.changed) {
    await updateDoc(teamRef, {
      'subscriptions.tagOverrides':
        Object.keys(subsMonthly.next).length > 0 ? subsMonthly.next : deleteField(),
      'subscriptions.tagOverridesYearly':
        Object.keys(subsYearly.next).length > 0 ? subsYearly.next : deleteField(),
    });
  }
}

// Main function: remove deleted tags from all team members
export async function removeDeletedTagsFromMembers(teamId, oldTags = [], newTags = []) {
  const deleted = getDeletedTagNames(oldTags, newTags);
  if (!deleted.length) return;

  const snapshot = await getDocs(collection(db, 'teams', teamId, 'members'));

  const updates = snapshot.docs.map(async (docSnap) => {
    const data = docSnap.data();
    const userTags = data.tags || {};
    let changed = false;

    for (const tag of deleted) {
      if (Object.prototype.hasOwnProperty.call(userTags, tag)) {
        delete userTags[tag];
        changed = true;
      }
    }

    if (changed) {
      await updateDoc(docSnap.ref, { tags: userTags });
    }
  });

  await Promise.all(updates);
}
